import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { ReactComponent as ArrowBack } from '../../../assets/images/icon-arrow-back-color-primary.svg';
import Typography from '../../../components/typography';
import ContentSection from '../../../components/contentSection';
import Button from '../../../components/button';
import OtpBoxes from '../../../components/otpBoxes';
import { LogoutAction } from '../../../store/actions/auth.actions';
import { logout } from '../../../utils/auth';
import { deleteMFAService } from '../../../services/user';

import './security.scss';

const Security = ({ isMobileDevice }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showConfirm, setShowConfirm] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  const handleCancel = () => {
    setShowConfirm(false);
    setOtp('');
    setOtpError('');
  };

  useEffect(() => {
    if (otp?.length === 5) {
      (() => setOtpError(''))();
    }
  }, [otp]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await deleteMFAService(otp);
      logout();
      dispatch(LogoutAction());
    } catch {
      setOtpError('No fue posible eliminar el 2FA. Verifica el código e intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentSection
      className="securitySection"
      headerChildrenLeft={(
        <div className="containerHeader">
          <ArrowBack onClick={() => navigate(-1)} className="arrowBack" />
          <Typography
            type="superTitle-italic"
            text="Seguridad"
          />
        </div>
      )}
    >
      <div className={`security-screen${isMobileDevice ? ' mobile' : ''}`}>
        <div className="security__header">
          <div className="security__header-icon">
            <div />
          </div>
          <div className="security__header-text">
            <Typography
              type="title"
              text="Seguridad y autenticación"
              className="security__header-title"
            />
            <Typography
              type="subtitle"
              text="Gestiona la configuración de seguridad de tu cuenta"
              className="security__header-subtitle"
            />
          </div>
        </div>

        <div className="security__card security__card--2fa">
          <div className="security__card-top">
            <div className="security__card-icon">
              <div />
            </div>
            <div className="security__card-info">
              <Typography
                type="title"
                text="Autenticación de dos factores (2FA)"
                className="security__card-title"
              />
              <Typography
                type="subtitle"
                text="Tu cuenta está protegida con autenticación de dos factores."
                className="security__card-subtitle"
              />
            </div>
            {!showConfirm && (
              <div className="security__card-action">
                <Button
                  type="primary-second"
                  text="Eliminar 2FA"
                  className=""
                  onClick={() => setShowConfirm(true)}
                />
              </div>
            )}
          </div>

          {showConfirm && (
            <div className="security__confirm">
              <hr className="security__divider" />
              <Typography
                type="subtitle"
                className="security__confirm-text"
              >
                {'Para desactivar la verificación en dos pasos, ingresa el código actual que muestra tu app de autenticación. Al confirmarlo se '}
                <span className="security__confirm-warning">cerrará tu sesión</span>
                {' y deberás configurar el 2FA nuevamente.'}
              </Typography>
              <div>
                <OtpBoxes onChange={setOtp} />
                {otpError && otp?.length > 5 && (
                  <Typography
                    type="subtitle"
                    text={otpError}
                    className="security__otp-error"
                  />
                )}
              </div>
              <div className="security__confirm-actions">
                <Button
                  type="tertiary"
                  text="Cancelar"
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={loading}
                />
                <div>
                  <Button
                    type="primary-second"
                    text="Confirmar eliminación"
                    className=""
                    onClick={handleConfirm}
                    disabled={otp.length < 6}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="security__card security__card--why">
          <Typography
            type="title"
            text="¿Por qué usar 2FA?"
            className="security__why-title"
          />
          <ul>
            <li>
              <Typography type="subtitle" text="Protege tu cuenta incluso si alguien conoce tu contraseña." />
            </li>
            <li>
              <Typography type="subtitle" text="Compatible con Google Authenticator, Authy, Microsoft Authenticator y más." />
            </li>
            <li>
              <Typography type="subtitle" text="Los códigos cambian cada 30 segundos para máxima seguridad." />
            </li>
          </ul>
        </div>
      </div>
    </ContentSection>
  );
};

Security.propTypes = {
  isMobileDevice: PropTypes.bool.isRequired,
};

export default Security;
